import type { Issue, IssueSort, IssueStatus } from '@outpost/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BellOff, Check, PartyPopper, Radio, Search } from 'lucide-react';
import { useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';

import { AssigneeFilter, AssigneePicker } from '@/components/assignee';
import { FilterDropdown } from '@/components/filter-dropdown';
import { LevelBadge } from '@/components/level-badge';
import { SdkInstall } from '@/components/sdk-install';
import { Sparkline } from '@/components/sparkline';
import { EmptyState, ErrorState, ListSkeleton } from '@/components/states';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useAuth } from '@/lib/auth';
import { absoluteTime, compactNumber, relativeTime, splitTitle } from '@/lib/format';
import {
  ASSIGNEE,
  ENVIRONMENT,
  PLATFORM,
  RELEASE,
  filterValues,
  withFilterValues,
} from '@/lib/query';
import { cn, focusRing } from '@/lib/utils';

/** The longest window the issue list accepts. */
const ANY_ISSUE_HOURS = 24 * 90;

/** Saved searches covering the queries people actually run during triage. */
const PRESETS = [
  { label: 'Unresolved', query: 'is:unresolved' },
  { label: 'Unhandled', query: 'is:unresolved is:unhandled' },
  { label: 'Errors and worse', query: 'is:unresolved level:>=error' },
  { label: 'All', query: '' },
];

/**
 * Quick filters scope the view rather than define it, so presets keep them and
 * still count as active alongside them.
 */
const SCOPES = [ENVIRONMENT, RELEASE, PLATFORM, ASSIGNEE];

export function IssuesPage() {
  const { projectId = '' } = useParams();
  const { client } = useAuth();
  const queryClient = useQueryClient();

  // The query lives in the URL so a search can be shared in an incident channel.
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get('query') ?? 'is:unresolved';
  const sort = (searchParams.get('sort') as IssueSort | null) ?? 'last_seen';

  const [draft, setDraft] = useState(query);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const issues = useQuery({
    queryKey: ['issues', projectId, query, sort],
    queryFn: () => client.listIssues(projectId, { query, sort, limit: 50, hours: 24 * 14 }),
    // An empty list is often a project waiting on its first event, so it keeps
    // looking rather than making someone reload after sending one.
    refetchInterval: (current) => (current.state.data?.items.length === 0 ? 10_000 : false),
  });

  // An empty search does not say whether the search was too narrow or nothing
  // has arrived at all, so only then ask whether the project has any issue —
  // over the longest window the API allows, since the list only covers two weeks.
  const searchEmpty = issues.isSuccess && issues.data.items.length === 0;
  const anyIssue = useQuery({
    queryKey: ['issues', projectId, 'any'],
    queryFn: () => client.listIssues(projectId, { query: '', limit: 1, hours: ANY_ISSUE_HOURS }),
    enabled: searchEmpty,
    refetchInterval: (current) => (current.state.data?.items.length === 0 ? 10_000 : false),
  });
  const awaitingFirstIssue = searchEmpty && anyIssue.data?.items.length === 0;

  const environments = useQuery({
    queryKey: ['tag-values', projectId, 'environment'],
    queryFn: () => client.tagValues(projectId, 'environment'),
    staleTime: 5 * 60_000,
  });
  const platforms = useQuery({
    queryKey: ['tag-values', projectId, 'platform'],
    queryFn: () => client.tagValues(projectId, 'platform'),
    staleTime: 5 * 60_000,
  });
  // The releases list rather than tag values: it is newest first, where tag
  // values sort alphabetically and put 1.10 before 1.9.
  const releases = useQuery({
    queryKey: ['releases', projectId],
    queryFn: () => client.listReleases(projectId),
    staleTime: 5 * 60_000,
  });
  const releaseVersions = [...new Set(releases.data?.map((release) => release.version))];

  const selectedEnvironments = filterValues(query, ENVIRONMENT);
  const selectedReleases = filterValues(query, RELEASE);
  const selectedPlatforms = filterValues(query, PLATFORM);
  // One value only: a second `assigned:` term would silently override the first.
  const selectedAssignee = filterValues(query, ASSIGNEE).at(-1) ?? null;
  const scoped = (base: string) =>
    SCOPES.reduce(
      (next, filter) => withFilterValues(next, filter, filterValues(query, filter)),
      base,
    );
  const unscoped = SCOPES.reduce((next, filter) => withFilterValues(next, filter, []), query);

  const updateStatus = useMutation({
    mutationFn: (input: { ids: string[]; status: IssueStatus }) =>
      client.bulkUpdate(projectId, input.ids, input.status),
    onSuccess: () => {
      setSelected(new Set());
      void queryClient.invalidateQueries({ queryKey: ['issues', projectId] });
    },
  });

  const setQuery = (next: string) => {
    const params = new URLSearchParams(searchParams);
    // Kept even when empty: a missing param means the `is:unresolved` default,
    // so deleting it would turn "All" back into "Unresolved".
    params.set('query', next);
    setSearchParams(params, { replace: true });
  };

  const applyQuery = (next: string) => {
    setDraft(next);
    setQuery(next);
  };

  const toggle = (id: string) => {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const items = issues.data?.items ?? [];
  const allSelected = items.length > 0 && selected.size === items.length;

  return (
    <div className="space-y-4">
      <form
        onSubmit={(event) => {
          event.preventDefault();
          setQuery(draft);
        }}
        className="flex flex-wrap items-center gap-2"
      >
        <div className="relative min-w-0 basis-full sm:min-w-[18rem] sm:basis-auto sm:flex-1">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            placeholder="is:unresolved level:>=error release:1.4.2"
            className="pl-8 font-mono text-xs"
            aria-label="Search issues"
          />
        </div>

        <Select
          value={sort}
          onValueChange={(value) => {
            const params = new URLSearchParams(searchParams);
            params.set('sort', value);
            setSearchParams(params, { replace: true });
          }}
        >
          <SelectTrigger className="min-w-0 flex-1 sm:w-40 sm:flex-none">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="last_seen">Last seen</SelectItem>
            <SelectItem value="first_seen">First seen</SelectItem>
            <SelectItem value="events">Events</SelectItem>
            <SelectItem value="users">Users</SelectItem>
          </SelectContent>
        </Select>

        <Button type="submit" variant="secondary">
          Search
        </Button>
      </form>

      <div className="flex flex-wrap items-center gap-1.5">
        {PRESETS.map((preset) => (
          <button
            key={preset.label}
            type="button"
            onClick={() => applyQuery(scoped(preset.query))}
            className={cn(
              'rounded-full border px-2.5 py-1 text-xs transition-colors',
              focusRing,
              unscoped === preset.query
                ? 'border-primary bg-primary font-medium text-primary-foreground'
                : 'border-border bg-card text-muted-foreground hover:border-input-hover hover:text-foreground',
            )}
          >
            {preset.label}
          </button>
        ))}

        <div className="flex flex-wrap gap-1.5 sm:ml-auto">
          <FilterDropdown
            label="Environment"
            options={environments.data ?? []}
            selected={selectedEnvironments}
            onChange={(values) => applyQuery(withFilterValues(query, ENVIRONMENT, values))}
          />
          <FilterDropdown
            label="Release"
            options={releaseVersions}
            selected={selectedReleases}
            onChange={(values) => applyQuery(withFilterValues(query, RELEASE, values))}
          />
          <FilterDropdown
            label="Platform"
            options={platforms.data ?? []}
            selected={selectedPlatforms}
            onChange={(values) => applyQuery(withFilterValues(query, PLATFORM, values))}
          />
          <AssigneeFilter
            projectId={projectId}
            selected={selectedAssignee}
            onChange={(value) =>
              applyQuery(withFilterValues(query, ASSIGNEE, value === null ? [] : [value]))
            }
          />
        </div>
      </div>

      {selected.size > 0 && (
        <div className="flex flex-wrap items-center gap-2 rounded-md border border-border bg-card px-3 py-2">
          <span className="text-sm text-muted-foreground">{selected.size} selected</span>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => updateStatus.mutate({ ids: [...selected], status: 'resolved' })}
            disabled={updateStatus.isPending}
          >
            <Check />
            Resolve
          </Button>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => updateStatus.mutate({ ids: [...selected], status: 'ignored' })}
            disabled={updateStatus.isPending}
          >
            <BellOff />
            Ignore
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())}>
            Clear
          </Button>
        </div>
      )}

      {issues.isLoading && <ListSkeleton />}
      {issues.isError && <ErrorState error={issues.error} onRetry={() => void issues.refetch()} />}

      {searchEmpty && anyIssue.isPending && <ListSkeleton />}
      {awaitingFirstIssue && <AwaitingFirstIssue projectId={projectId} />}

      {searchEmpty && (anyIssue.isError || (anyIssue.data?.items.length ?? 0) > 0) && (
        <EmptyState
          icon={<PartyPopper className="size-5" />}
          title="Nothing to triage"
          description="No issues match this search. Try widening it, or clear the filters."
        />
      )}

      {items.length > 0 && (
        <div className="overflow-hidden rounded-lg border border-border bg-card">
          <div className="flex items-center gap-3 border-b border-border bg-muted px-3 py-2 text-xs text-muted-foreground">
            <input
              type="checkbox"
              checked={allSelected}
              onChange={() =>
                setSelected(allSelected ? new Set() : new Set(items.map((issue) => issue.id)))
              }
              aria-label="Select all issues"
              className="size-3.5 accent-control"
            />
            <span className="flex-1">Issue</span>
            <span className="w-6 text-center" title="Assignee">
              <span className="sr-only">Assignee</span>
            </span>
            <span className="hidden w-[120px] text-center md:block">Last 24h</span>
            <span className="w-12 text-right sm:w-16">Events</span>
            <span className="hidden w-16 text-right sm:block">Users</span>
            <span className="w-10 text-right sm:w-14">Age</span>
          </div>

          {items.map((issue) => (
            <IssueRow
              key={issue.id}
              issue={issue}
              projectId={projectId}
              selected={selected.has(issue.id)}
              onToggle={() => toggle(issue.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Setup instructions in place of the issue list, for a project with no issues
 * at all: the people looking at an empty project are almost always the ones
 * about to send it something.
 */
function AwaitingFirstIssue({ projectId }: { projectId: string }) {
  const { client } = useAuth();

  const project = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => client.getProject(projectId),
  });
  const keys = useQuery({
    queryKey: ['keys', projectId],
    queryFn: () => client.listKeys(projectId),
  });
  const dsn = keys.data?.find((key) => key.is_active)?.dsn ?? '';
  // Issues older than the window are not looked for, so a long-quiet project
  // is not described as one that never received anything.
  const quiet =
    project.data &&
    Date.now() - new Date(project.data.created_at).getTime() > ANY_ISSUE_HOURS * 3_600_000;

  let description =
    'Nothing has been reported to this project yet. Install the SDK for your platform with a snippet below; issues show up here as soon as the first error arrives.';
  if (quiet) {
    description =
      'Nothing has been reported to this project in the last 90 days. Check that your SDK is installed with a snippet below; issues show up here as soon as an error arrives.';
  }
  if (project.data && !project.data.is_active) {
    description =
      'This project is archived, so its DSN keys are refused. Unarchive it in its settings before sending events.';
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Radio className="size-4" />
          {quiet ? 'No recent issues' : 'No issues yet'}
        </CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent>
        {keys.isError ? (
          <ErrorState error={keys.error} onRetry={() => void keys.refetch()} />
        ) : (
          <SdkInstall dsn={dsn} platforms={project.data?.platforms} />
        )}
      </CardContent>
    </Card>
  );
}

function IssueRow({
  issue,
  projectId,
  selected,
  onToggle,
}: {
  issue: Issue;
  projectId: string;
  selected: boolean;
  onToggle: () => void;
}) {
  const { client } = useAuth();
  const { type, message } = splitTitle(issue.title);

  // Sparklines load per row rather than with the list: the list must render
  // immediately, and a chart appearing a moment later is not disruptive.
  const stats = useQuery({
    queryKey: ['issue-stats', issue.id],
    queryFn: () => client.issueStats(issue.id, 24),
    staleTime: 60_000,
  });

  return (
    <div
      className={cn(
        'flex items-center gap-2 border-b border-border px-3 py-2.5 last:border-b-0 transition-colors hover:bg-accent/40 sm:gap-3',
        selected && 'bg-brand-subtle hover:bg-brand-subtle',
        issue.status !== 'unresolved' && 'opacity-60',
      )}
    >
      <input
        type="checkbox"
        checked={selected}
        onChange={onToggle}
        aria-label={`Select ${issue.title}`}
        className="size-3.5 accent-control"
      />

      <div className="min-w-0 flex-1">
        <Link
          to={`/projects/${projectId}/issues/${issue.id}`}
          className="flex items-baseline gap-2 hover:underline"
        >
          {type && <span className="max-w-full shrink-0 truncate font-medium">{type}</span>}
          <span className={cn('truncate text-sm', type ? 'text-muted-foreground' : 'font-medium')}>
            {message}
          </span>
        </Link>

        <div className="mt-1 flex min-w-0 items-center gap-2 text-xs text-muted-foreground">
          <LevelBadge level={issue.level} />
          {issue.culprit && <span className="truncate font-mono">{issue.culprit}</span>}
          {issue.status === 'resolved' && <span className="text-level-info">resolved</span>}
          {issue.status === 'ignored' && <span>ignored</span>}
        </div>
      </div>

      <div className="flex w-6 justify-center">
        <AssigneePicker
          projectId={projectId}
          issueId={issue.id}
          assigneeId={issue.assignee_id}
          compact
        />
      </div>

      <div className="hidden w-[120px] md:block">
        <Sparkline buckets={stats.data ?? []} />
      </div>

      <span className="w-12 text-right text-sm tabular-nums sm:w-16">
        {compactNumber(issue.times_seen)}
      </span>
      <span className="hidden w-16 text-right text-sm tabular-nums text-muted-foreground sm:block">
        {compactNumber(issue.users_affected)}
      </span>
      <span
        className="w-10 text-right text-sm tabular-nums text-muted-foreground sm:w-14"
        title={absoluteTime(issue.last_seen)}
      >
        {relativeTime(issue.last_seen)}
      </span>
    </div>
  );
}
